import { makeStyles } from "tss-react/mui"
import { DialogContent, Stack, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { UserType } from "../../../../modules/login/loginTypes"
import { StandardButton } from "../../buttons/StandardButton"
import { StandardDialog } from "../../dialogs/StandardDialog"
import { OnBehalfCompletedDialog } from "../hooks/useCreateOrderUser"
import { CreateOrderDialogMode } from "../createOrderTypes"
import { CreateOrderType, MEETING_SERVICE_SUPERUSER_TYPES } from "../helpers"

function getBodyTranslationId(
    useNonCreateBody: boolean,
    useAdminCreateBody: boolean,
    isMeetingService: boolean,
    isResourceBooking: boolean,
) {
    if (useNonCreateBody) return "create-order-invalid-user-permissions-non-create-dialog-body"
    if (useAdminCreateBody && isMeetingService)
        return "create-order-invalid-user-permissions-merchant-meeting-service-dialog-body"
    if (useAdminCreateBody && isResourceBooking)
        return "create-order-invalid-user-permissions-merchant-resource-booking-dialog-body"
    return "create-order-tabs-error-body"
}

const useStyles = makeStyles()((theme) => ({
    dialogContent: {
        padding: "2.5rem",
        paddingTop: "1rem",
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        margin: "0 auto",
        color: theme.palette.tailwind.slate[950],
    },
}))

type InvalidUserPermissionsDialogProps = Readonly<{
    isOnBehalfCompleted: OnBehalfCompletedDialog
    activeOrderTab?: CreateOrderType
    mode?: CreateOrderDialogMode
    onBehalfDisplayName?: string
    handleClose: () => void
    handleShowOrderOnBehalfDialog: () => void
}>

export function InvalidUserPermissionsDialog({
    isOnBehalfCompleted,
    activeOrderTab,
    mode,
    onBehalfDisplayName,
    handleClose,
    handleShowOrderOnBehalfDialog,
}: InvalidUserPermissionsDialogProps) {
    const { classes } = useStyles()
    const { userType } = isOnBehalfCompleted

    const isAdminLike = MEETING_SERVICE_SUPERUSER_TYPES.includes(userType)
    const isUser = userType == UserType.USER
    const isCreate = mode === CreateOrderDialogMode.CREATE || mode === undefined
    const isUserSelectionLocked = mode === CreateOrderDialogMode.DUPLICATE || mode === CreateOrderDialogMode.EDIT
    const isMeetingService = activeOrderTab === CreateOrderType.MEETING_SERVICE
    const isResourceBooking = activeOrderTab === CreateOrderType.RESOURCE_BOOKING

    const displayName = onBehalfDisplayName ?? isOnBehalfCompleted.displayName

    const useAdminCreateBody = isCreate && isAdminLike && (isMeetingService || isResourceBooking)
    const useNonCreateBody = !isCreate

    const bodyTranslationId = getBodyTranslationId(
        useNonCreateBody,
        useAdminCreateBody,
        isMeetingService,
        isResourceBooking,
    )

    const showDisplayNameVar = useAdminCreateBody || useNonCreateBody
    const bodyVars = showDisplayNameVar ? { displayName } : undefined

    const bodyElements = showDisplayNameVar
        ? {
              displayName: (
                  <Typography component="span" fontWeight="bold">
                      {displayName}
                  </Typography>
              ),
          }
        : undefined

    const titleTranslationId = isCreate
        ? "create-order-tabs-error"
        : "create-order-invalid-user-permissions-non-create-dialog-title"
    const titleFallback = isCreate ? "You can't place an order" : "Access restriction"

    return (
        <StandardDialog
            dialogTitle={
                <LocalizedStrict id={titleTranslationId}>
                    <>{titleFallback}</>
                </LocalizedStrict>
            }
            open={true}
            onClose={() => {}}
            hideCloseIcon
            maxWidthProp="sm"
            scroll="body"
        >
            <DialogContent className={classes.dialogContent}>
                <LocalizedStrict id={bodyTranslationId} vars={bodyVars} elems={bodyElements}>
                    <Typography variant="body1" textAlign="center">
                        You can't place an order because your account doesn't have the required access. Please contact
                        support if you need more information.
                    </Typography>
                </LocalizedStrict>
                <Stack flexDirection="row" gap="1rem" justifyContent="center">
                    <LocalizedStrict id="create-order-tabs-error-dialog-close-button">
                        <StandardButton color="grey" variant="outlined" fullWidth={!isUser} onClick={handleClose}>
                            Go back
                        </StandardButton>
                    </LocalizedStrict>
                    {!isUser && !isUserSelectionLocked && (
                        <LocalizedStrict id="create-order-tabs-error-dialog-confirm-button">
                            <StandardButton onClick={handleShowOrderOnBehalfDialog}>Select another user</StandardButton>
                        </LocalizedStrict>
                    )}
                </Stack>
            </DialogContent>
        </StandardDialog>
    )
}
